// Small, content-fit filled CTA mirroring the Tessell portal `btnPri` token.
export const commonButtonStyles = (colors, m, hue) => ({
  m: m || "0 auto",
  width: "fit-content",
  maxWidth: "100%",
  height: "32px",
  minHeight: "32px",
  padding: "0 12px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  border: "1px solid transparent",
  borderRadius: "6px",
  fontSize: 11,
  fontWeight: 500,
  color: "#fff",
  whiteSpace: "nowrap",
  backgroundColor: (hue && hue[500]) || colors.ctaBg,
  borderColor: (hue && hue[500]) || colors.ctaBg,
  transition: "background 120ms ease",
  "&:hover": {
    backgroundColor: (hue && hue[700]) || colors.ctaHover,
    borderColor: (hue && hue[700]) || colors.ctaHover,
    cursor: "pointer",
  },
  "&:disabled": {
    backgroundColor: colors.grey[600],
    borderColor: colors.grey[600],
    color: "#fff",
    opacity: 0.7,
    "&:hover": {
      cursor: "auto",
    },
  },
});

// Environments list page button restyle — single source of truth for the row
// action buttons (HIBERNATE / WAKE UP / REFRESH) and the toolbar buttons
// (RELOAD / CREATE NEW). Icons keep their own size/position; they only inherit
// the button's text color.
const envListButtonBase = {
  height: "32px",
  minHeight: "32px",
  borderRadius: "6px",
  fontSize: "11px",
  fontWeight: 600,
  letterSpacing: "0.05em",
  lineHeight: 1,
  textTransform: "uppercase",
  padding: "0 14px",
  "& .MuiTypography-root": {
    font: "inherit",
    color: "inherit",
    letterSpacing: "inherit",
    lineHeight: "inherit",
    textTransform: "inherit",
  },
  "& svg": { color: "inherit" },
};

// The table body carries no filled buttons: both row actions are outlined, the
// state action leading with the action blue so it still reads first.
const rowActionBase = (colors) => ({
  ...envListButtonBase,
  fontWeight: 500,
  height: "28px",
  minHeight: "28px",
  padding: "0 11px",
  backgroundColor: colors.surface,
  borderColor: colors.ctl,
  "&:disabled": {
    backgroundColor: colors.disBg,
    borderColor: colors.disBorder,
    color: colors.disFg,
    opacity: 1,
    "&:hover": {
      backgroundColor: colors.disBg,
      borderColor: colors.disBorder,
      cursor: "not-allowed",
    },
  },
});

export const envListButtonStyles = (colors) => ({
  rowActionState: {
    ...rowActionBase(colors),
    color: colors.btnHover,
    "&:hover": {
      backgroundColor: colors.actionTintBg,
      borderColor: colors.btn,
      cursor: "pointer",
    },
  },
  rowActionNeutral: {
    ...rowActionBase(colors),
    color: colors.ctlInk,
    "&:hover": {
      backgroundColor: colors.ctlHoverBg,
      borderColor: colors.ctl,
      color: colors.ctlInkHover,
      cursor: "pointer",
    },
  },
  toolbarOutlined: {
    ...envListButtonBase,
    padding: "0 12px",
    backgroundColor: colors.surface,
    borderColor: colors.ctl,
    color: colors.ctlInk,
    "&:hover": {
      backgroundColor: colors.ctlHoverBg,
      borderColor: colors.ctl,
      cursor: "pointer",
    },
    "&:disabled": {
      backgroundColor: colors.surface,
      borderColor: colors.ctl,
      color: colors.disFg,
      opacity: 1,
      "&:hover": { cursor: "auto" },
    },
  },
  toolbarPrimary: {
    ...envListButtonBase,
    fontWeight: 500,
    backgroundColor: colors.btn,
    borderColor: colors.btn,
    // Ink on a filled brand button stays white in both modes.
    color: "#FFFFFF",
    "&:hover": {
      backgroundColor: colors.btnHover,
      borderColor: colors.btnHover,
      cursor: "pointer",
    },
  },
});

// IAM is rare and consequential, so it does not compete with the page's primary
// action as a second filled blue button. Default for useIamModal, shared by
// every page that opens an IAM modal.
// `m` must be a string: DataGridButton lifts it onto its wrapper via
// `m || "0 auto"`, so 0 is falsy and would leave an auto margin that eats the
// free space of whatever flex row the button sits in.
// Pairs with iamButtonStyles: sets IAM apart from the page's own actions so it
// never abuts a destructive one. Render it only when the IAM button renders,
// or it dangles for users without the permission.
export const iamDividerStyles = (colors) => ({
  flexShrink: 0,
  width: "2px",
  height: "20px",
  mx: "8px",
  my: "auto",
  backgroundColor: colors.border,
});

export const iamButtonStyles = (colors) => ({
  m: "0px",
  backgroundColor: colors.surface,
  borderColor: colors.ctl,
  "& .MuiTypography-root": { color: colors.sec },
  "&:hover": {
    backgroundColor: colors.divider,
    borderColor: colors.ctl,
    "& .MuiTypography-root": { color: colors.body },
  },
});

// Drawer/dialog close (×) button — the bordered, hover-highlighted style
// RefreshDrawer introduced (see RefreshDrawer/styles.js closeButton). Shared
// here so every other drawer's close IconButton can look the same instead of
// falling back to a bare unstyled icon.
export const closeButtonStyles = (colors) => ({
  flexShrink: 0,
  width: "30px",
  height: "30px",
  borderRadius: "6px",
  border: `1px solid ${colors.ctl}`,
  color: colors.railMuted,
  "&:hover": {
    backgroundColor: colors.openRow,
    borderColor: colors.btn,
    color: colors.btn,
  },
});

export const dataGridButtonStyles = {
  pageTopButtons: {
    m: "0px 0 0 2px",
    width: "fit-content",
  },
  refreshButton: {
    width: "fit-content",
  },
  wakeUpButton: {
    width: "fit-content",
  },
  patchButton: {
    width: "fit-content",
  },
  requestApproveButton: {
    m: "5px 0 0 5px",
    width: "fit-content",
  },
};
